import {
  buildRequestURL,
  getRESTParams,
  postRESTParams,
  REMBLE_API_URL,
  NO_LIMIT
} from '.'

const BASE_PATH = `${REMBLE_API_URL}/users/responses`

const RESPONSE_TYPE_ASSESSMENT = 'assessment_answer'
const RESPONSE_TYPE_ESSAY = 'essay'
const RESPONSE_TYPE_SINGLE = 'single'

const ERR_RESPONSE_ALREADY_EXISTS = 'User response already exists'

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Common User Responses GET request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See getRESTParams method for more info.
 */
const _responsesRESTGET = async (url, params) => {
  const result = await fetch(url, getRESTParams(params))
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
      return { error }
    })

  return result
}

/**
 * Common User Responses POST request pattern.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _responsesRESTPOST = async (url, params) =>
  _responsesRESTGET(
    url,
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Posts user response data to the /users/responses endpoint to save user responses
 * @param {Number} quizId The quiz the question belongs to
 * @param {Number} questionId The question to save
 * @param {String} responseText The response text to save
 * @param {Number} responseId The response id, if updating an existing response
 * @returns Returns an object with an error or an object with the post response data
 */
const saveUserResponse = async (
  quizId,
  questionId,
  responseText,
  responseId = 0
) => {
  let url,
    body,
    method = 'POST'

  // Updating or creating?
  if (responseId) {
    url = `${BASE_PATH}/update`
    body = JSON.stringify({
      id: responseId,
      response_text: responseText
    })
    method = 'PATCH'
  } else {
    url = `${BASE_PATH}/create`
    body = JSON.stringify({
      quiz_id: quizId,
      question_id: questionId,
      response_text: responseText
    })
  }

  // Try to save the response
  let saved = await _responsesRESTPOST(url, { body, method }).catch(error => {
    console.error('error', error)
    return { error }
  })

  // Check for duplicate response error
  if (saved?.detail && saved.detail.includes(ERR_RESPONSE_ALREADY_EXISTS)) {
    // If duplicate response, recurse to update the response, but first get the response id
    const responses = await getUserResponses({ quizId, questionId })

    // Make sure we got a response
    if (responses?.responses?.length) {
      const response = responses.responses[0]
      const responseId = response.id

      // Update the response
      if (responseId)
        return saveUserResponse(quizId, questionId, responseText, response.id)
    }
  }

  return saved
}

/**
 * Gets user responses from the /users/responses endpoint
 * @param {Object} filters The filters to use when searching for responses
 * @returns Returns an object with an error or an object with the post response data
 */
const getUserResponses = async ({ quizId, questionId = 0 }) => {
  const url = `${BASE_PATH}/find`
  const filters = {
    limit: Math.max(NO_LIMIT, 10_000) // NO_LIMIT may be limited by LearnDash
  }

  if (quizId) filters['quiz_id'] = quizId
  if (questionId) filters['question_id'] = questionId

  const requestUrl = buildRequestURL(url, filters)

  const saved = await _responsesRESTGET(requestUrl).catch(error => {
    console.error('error', error)
    return { error }
  })

  return saved
}

export {
  getUserResponses,
  saveUserResponse,
  RESPONSE_TYPE_ASSESSMENT,
  RESPONSE_TYPE_ESSAY,
  RESPONSE_TYPE_SINGLE
}
